import {
  productSlug,
  type Product,
} from "@exxatdesignux/product-framework/app-store"
import {
  isBuiltInProductSlug,
  resolveProductFromPath,
  type ResolvedProductRoute,
} from "@exxatdesignux/product-framework/product-routing"
import type { CustomProductBrand } from "@exxatdesignux/product-framework/app-store"

import { isProductsHomePath } from "@/lib/product-home"
import { appPathname } from "@/lib/app-pathname"
import { isPrismSuiteProduct, PRISM_SUITE_PRODUCTS } from "@/lib/prism-suite-products"

/** Product theme classes that may appear on `<html>`. */
export const PRODUCT_THEME_CLASSES = [
  "theme-one",
  "theme-lavender",
  "theme-prism",
  "theme-rose",
  "theme-custom",
] as const

export type ProductThemeClass = (typeof PRODUCT_THEME_CLASSES)[number]

const PRISM_SUITE_ROUTE_SLUGS = new Set(
  [...PRISM_SUITE_PRODUCTS].map(product => productSlug(product)),
)

/**
 * Built-in Prism suite route, even when tenant custom products mirror the same
 * slug (`Compliance` → `/compliance`). `resolveProductFromPath` prefers those
 * custom rows and would otherwise leave the shell on `theme-custom` green.
 */
export function isBuiltInPrismSuiteRoute(pathname: string): boolean {
  if (isProductsHomePath(pathname)) return true
  const segment = pathname.replace(/^\/+/, "").split("/", 1)[0] ?? ""
  if (!segment || !isBuiltInProductSlug(segment)) return false
  return PRISM_SUITE_ROUTE_SLUGS.has(segment)
}

/** Shared Prism pink chrome for home and every suite app. */
export function prismDocumentThemeSnapshot(): {
  expectedTheme: ProductThemeClass
  expectedBrandColor: string
} {
  return {
    expectedTheme: "theme-prism",
    expectedBrandColor: "#E31C79",
  }
}

export function applyPrismDocumentTheme() {
  const html = document.documentElement
  for (const themeClass of PRODUCT_THEME_CLASSES) {
    html.classList.remove(themeClass)
  }
  const { expectedTheme, expectedBrandColor } = prismDocumentThemeSnapshot()
  html.style.setProperty("--custom-product-brand-color", expectedBrandColor)
  html.classList.add(expectedTheme)
  html.dataset.prismDocumentTheme = "active"
}

export function usesPrismDocumentTheme(
  pathname: string,
  storeProduct: Product,
): boolean {
  return isBuiltInPrismSuiteRoute(pathname) || isPrismSuiteProduct(storeProduct)
}

/**
 * Tenant catalog mirrors (`Compliance` → `/compliance`) shadow built-in products
 * in `resolveProductFromPath`. On Prism suite slugs, prefer the shipped product.
 */
export function resolveProductFromPathPreferringBuiltInPrism(
  pathname: string,
  customProducts: CustomProductBrand[],
): ResolvedProductRoute | null {
  if (isBuiltInPrismSuiteRoute(pathname)) {
    const builtIn = resolveProductFromPath(pathname, [])
    if (builtIn && builtIn.product !== "exxat-custom") {
      return builtIn
    }
  }
  return resolveProductFromPath(pathname, customProducts)
}

export function documentThemeNeedsPrismResync(): boolean {
  const html = document.documentElement
  if (!isBuiltInPrismSuiteRoute(appPathname())) return false
  if (!html.classList.contains("theme-prism")) return true
  if (html.classList.contains("theme-custom")) return true
  const brand = html.style.getPropertyValue("--custom-product-brand-color").trim()
  if (brand && brand !== "#E31C79" && brand.toLowerCase() !== "#e31c79") {
    return true
  }
  return false
}
